/**
 * Role permission-grant matrix (AUTH-CHAT-WIRING-CONTRACT §2.4).
 *
 * Rendered on the role show page. Loads the role descriptor
 * (`GET /admin/roles/:name` → `static_rules` + `extra_grants`), its editable
 * grant keys (`GET /admin/roles/:name/permissions`), and the permissions catalog
 * (`GET /admin/permissions`). Renders a checkbox per catalog key (grouped by
 * resource) reflecting membership in the role's grant set, and saves the full
 * set via `PUT /admin/roles/:name/permissions { keys }`.
 *
 * The `admin` role is immutable (the worker returns 400) — all editing is
 * disabled for it and its `*:*` static rule is shown read-only instead.
 */
import * as React from "react";
import { useNotification } from "@refinedev/core";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { JsonViewer } from "@/components/app";
import { ErrorState } from "@/components/ui/states";

import { humanize } from "@/resources/projects/_form";
import { CheckRow } from "@/resources/admin/_controls";
import { LoadingState } from "@/resources/projects/_shared";
import {
  fetchAdminRole,
  fetchPermissionsCatalog,
  fetchRoleGrants,
  groupByResource,
  putRoleGrants,
  type PermissionRow,
} from "@/lib/admin-api";

export function RoleGrantMatrix({ roleName }: { roleName: string }) {
  const notify = useNotification();
  const isAdmin = roleName === "admin";

  const [catalog, setCatalog] = React.useState<PermissionRow[]>([]);
  const [granted, setGranted] = React.useState<Set<string>>(new Set());
  const [staticRules, setStaticRules] = React.useState<unknown[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [errored, setErrored] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      fetchPermissionsCatalog(controller.signal),
      fetchAdminRole(roleName, controller.signal),
      fetchRoleGrants(roleName, controller.signal),
    ])
      .then(([perms, role, keys]) => {
        setCatalog(perms);
        setStaticRules(role.static_rules ?? []);
        setGranted(new Set(keys));
      })
      .catch(() => setErrored(true))
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [roleName]);

  const toggle = (key: string, next: boolean) => {
    setGranted((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(key);
      else copy.delete(key);
      return copy;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await putRoleGrants(roleName, [...granted]);
      setGranted(new Set(res.keys));
      notify.open?.({
        type: "success",
        message: "Role grants updated",
        key: `role-grants-${roleName}`,
      });
    } catch (err) {
      notify.open?.({
        type: "error",
        message: "Failed to update grants",
        description: err instanceof Error ? err.message : String(err),
        key: `role-grants-err-${roleName}`,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState label="Loading permissions…" />;
  if (errored) {
    return (
      <ErrorState message="The permission matrix could not be loaded." />
    );
  }

  if (isAdmin) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Badge variant="success">Full access</Badge>
          <span className="text-sm text-muted-foreground">
            The admin role grants <span className="font-mono text-xs">*:*</span>{" "}
            and is immutable.
          </span>
        </div>
        {staticRules.length > 0 ? (
          <JsonViewer data={staticRules} />
        ) : null}
      </div>
    );
  }

  const groups = groupByResource(catalog);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Extra permission grants stored for this role, layered on top of its
        built-in rules. Tick a key to grant it; untick to remove.
      </p>

      <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
        {groups.map((group) => (
          <section key={group.resource} className="space-y-1">
            <h3 className="px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {humanize(group.resource)}
            </h3>
            {group.permissions.map((p) => (
              <CheckRow
                key={p.key}
                checked={granted.has(p.key)}
                onCheckedChange={(next) => toggle(p.key, next)}
                label={<span className="font-mono text-xs">{p.key}</span>}
                hint={p.description}
              />
            ))}
          </section>
        ))}
      </div>

      <div className="flex justify-end">
        <Button type="button" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save grants"}
        </Button>
      </div>
    </div>
  );
}

export default RoleGrantMatrix;
